import { makeAutoObservable } from "mobx";
import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import { peekQueryClient } from "@/queries/client";
import type { QueryScope } from "@/queries/scope";
import {
  findCachedTeam,
  isTeamsListKey,
  teamDetailKey,
  teamMembersKey,
  teamsBankKey,
  type TeamsInclude,
} from "@/queries/teams";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type {
  TCreateTeam,
  TDeleteTeamOptions,
  TMemberTeam,
  TMemberTeamChange,
  TTeam,
  TTeamMember,
  TTeamDeletePreflight,
  TTeamPermissionGraph,
  TTeamsPage,
  TUpdateTeam,
} from "@/types/team";

/** The include modes whose list entries embed rosters, so a roster write stales them. */
const MODES_WITH_ROSTERS: readonly TeamsInclude[] = ["members", "all+members"];

type KeyMatch = (queryKey: readonly unknown[]) => boolean;

/** Whether `queryKey` is in `scope`. */
function inScope(scope: QueryScope, queryKey: readonly unknown[]): boolean {
  return queryKey[0] === scope.tenant && queryKey[1] === scope.principal;
}

/** R18: any team's sub-teams. */
const isChildrenKey: KeyMatch = (key) => key[2] === "team" && key[4] === "children";
/** R17: any team's roster. */
const isRosterKey: KeyMatch = (key) =>
  key.length === 5 && key[2] === "team" && key[4] === "members";
/** R19: any member's teams. */
const isMemberTeamsKey: KeyMatch = (key) =>
  key.length === 5 && key[2] === "member" && key[4] === "teams";

/**
 * The workspace teams' writes. Team data itself is read only through Query
 * (NG-2796): the team lists (R14) through `useWorkspaceTeams` / `readTeams`,
 * the teams page (R15) through `useTeamsPage`, a team (R16), its roster (R17),
 * its sub-teams (R18) and a member's teams (R19) through `hooks/use-team.ts`.
 * Each write here patches or invalidates those cached entries (contract §3.2),
 * so every reader on screen shows a confirmed write.
 *
 * A write first cancels the reads in flight that it changes: an answer sent
 * for before the write would otherwise land over it (NG-2295, NG-2311). A
 * cancelled entry keeps what it held, the write's patch then applies to it,
 * and a loader whose read was cancelled asks again (`readTeams`).
 */
export class WorkspaceTeamsStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // Bound, so a caller can lift a writer off the instance.
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The client and scope `slug`'s cached team reads live under, or `null` before the session resolves. */
  private cache(slug: string): { client: QueryClient; scope: QueryScope } | null {
    const client = peekQueryClient();
    const scope = queryCacheScope(this.rootStore, slug, "workspace-teams", null);
    return client && scope ? { client, scope } : null;
  }

  /**
   * Cancel the reads in flight under `scope` that `matches`, so none lands over
   * the write about to be applied. An entry whose FIRST read was cancelled
   * holds nothing and has nothing in flight, so it reads again for its
   * observers, after the write.
   */
  private async cancelReads(
    client: QueryClient,
    scope: QueryScope,
    matches: KeyMatch,
  ): Promise<void> {
    const predicate = ({ queryKey }: Query) => inScope(scope, queryKey) && matches(queryKey);
    await client.cancelQueries({ predicate });
    await client.invalidateQueries({
      predicate: (query) => predicate(query) && query.state.data === undefined,
    });
  }

  /** Mark stale, and re-read on screen, every entry under `scope` that `matches`. */
  private invalidate(client: QueryClient, scope: QueryScope, matches: KeyMatch): Promise<void> {
    return client.invalidateQueries({
      predicate: ({ queryKey }) => inScope(scope, queryKey) && matches(queryKey),
    });
  }

  /** Apply `update` to every cached team list and page set (R14, R15) under `scope`. */
  private patchLists(
    client: QueryClient,
    scope: QueryScope,
    update: (rows: TTeam[]) => TTeam[],
    totalDelta: (page: TTeamsPage, rows: TTeam[]) => number = () => 0,
  ): void {
    const lists = client.getQueryCache().findAll({
      predicate: ({ queryKey }) => inScope(scope, queryKey) && isTeamsListKey(queryKey),
    });
    for (const query of lists) {
      if (query.queryKey[3] === "bank") {
        client.setQueryData<TTeam[]>(query.queryKey, (rows) => (rows ? update(rows) : rows));
        continue;
      }
      client.setQueryData<InfiniteData<TTeamsPage>>(query.queryKey, (set) =>
        set
          ? {
              ...set,
              pages: set.pages.map((page) => {
                const rows = update(page.results);
                const delta = totalDelta(page, rows);
                return {
                  ...page,
                  results: rows,
                  total_count: Math.max(0, page.total_count + delta),
                };
              }),
            }
          : set,
      );
    }
  }

  /** What `slug`'s team lists and sub-team lists need after a team is created, moved or deleted. */
  private async invalidateTree(slug: string): Promise<void> {
    const cache = this.cache(slug);
    if (!cache) return;
    await this.invalidate(
      cache.client,
      cache.scope,
      (key) => isTeamsListKey(key) || isChildrenKey(key),
    );
  }

  /**
   * What a confirmed change to who is on which team stales (contract §3.3): the
   * list entries that embed rosters (R14 with members), each named roster (R17),
   * and every member's team list (R19), since a team write names users while
   * R19 is keyed by membership.
   */
  private invalidateRosters(
    client: QueryClient,
    scope: QueryScope,
    teamIds: readonly string[],
  ): Promise<void> {
    const teams = new Set(teamIds);
    const banks = new Set(
      MODES_WITH_ROSTERS.map((include) => JSON.stringify(teamsBankKey(scope, include))),
    );
    return this.invalidate(
      client,
      scope,
      (key) =>
        banks.has(JSON.stringify(key)) ||
        (isRosterKey(key) && teams.has(key[3] as string)) ||
        isMemberTeamsKey(key),
    );
  }

  /** Log a cache step that failed after the write itself succeeded. */
  private logCacheFailure(what: string, context: Record<string, unknown>) {
    return (error: unknown) => {
      console.warn(`[workspace-teams] ${what} after a team write failed`, { ...context, error });
    };
  }

  async createTeam(
    slug: string,
    data: TCreateTeam,
    source: AnalyticsSource = "page",
  ): Promise<TTeam> {
    const created = await this.rootStore.workspaceTeamService.create(slug, data);
    track("team_created", { team_id: created.id, has_parent: Boolean(data.parent), source });
    const cache = this.cache(slug);
    if (cache) {
      await this.cancelReads(
        cache.client,
        cache.scope,
        (key) => isTeamsListKey(key) || isChildrenKey(key),
      );
      // Every list re-reads: where the new team sits, and in which modes and
      // searches, is the server's to say.
      this.invalidateTree(slug).catch(this.logCacheFailure("re-reading the team lists", { slug }));
    }
    return created;
  }

  /**
   * Save a change to one team. The saved row goes into its cached detail (R16)
   * and every list and page holding it (R14, R15), so a rename shows at once.
   * A move to another parent changes the tree's shape and depths, and a change
   * to the team's projects changes what the lists filter on, so both re-read
   * the lists and sub-team lists (R18). New leads change the roster's roles, so
   * the roster re-reads before the save settles.
   */
  async updateTeam(
    slug: string,
    id: string,
    data: TUpdateTeam,
    source: AnalyticsSource = "page",
  ): Promise<TTeam> {
    // Read before the write: `fields` is what this save changed (NG-2063).
    const before = findCachedTeam(id);
    const fields = changedFields(data, before);
    const updated = await this.rootStore.workspaceTeamService.update(slug, id, data);
    track("team_updated", { team_id: id, fields, source });
    const cache = this.cache(slug);
    if (!cache) return updated;
    const { client, scope } = cache;
    await this.cancelReads(
      client,
      scope,
      (key) => isTeamsListKey(key) || (key.length === 4 && key[2] === "team" && key[3] === id),
    );
    client.setQueryData<TTeam>(teamDetailKey(scope, id), (current) =>
      current ? { ...current, ...updated } : current,
    );
    this.patchLists(client, scope, (rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...updated } : row)),
    );
    const moved = data.parent !== undefined && (before?.parent ?? null) !== (data.parent ?? null);
    if (moved || data.project_ids !== undefined) {
      this.invalidateTree(slug).catch(
        this.logCacheFailure("re-reading the team lists", { slug, id }),
      );
    }
    if (data.lead_ids !== undefined) {
      // Awaited: a lead's role is on the roster, and the save is not done for
      // the Members table until it shows the new role.
      await this.invalidateRosters(client, scope, [id]).catch(
        this.logCacheFailure("re-reading the roster", { slug, id }),
      );
    }
    return updated;
  }

  async updateLeads(slug: string, id: string, leadIds: string[]): Promise<TTeam> {
    return this.updateTeam(slug, id, { lead_ids: leadIds });
  }

  async deleteTeam(
    slug: string,
    id: string,
    source: AnalyticsSource = "page",
    options?: TDeleteTeamOptions,
  ): Promise<void> {
    // Without options the call is the bare two-argument one it always was: the
    // flag-off dialog sends nothing new (NG-2157).
    if (options) {
      await this.rootStore.workspaceTeamService.remove(slug, id, options);
    } else {
      await this.rootStore.workspaceTeamService.remove(slug, id);
    }
    track("team_deleted", { team_id: id, source });
    await this.forgetDeletedTeam(slug, id);
  }

  /**
   * Drop a team the server has deleted from every cached read: its detail,
   * roster and sub-teams go, and its row leaves every list at once. The lists
   * then re-read, since a promoting delete (NG-2157) moved its sub-teams to the
   * top level, and the rosters and member team lists that named it re-read.
   * Public because a team can be deleted by more than `deleteTeam`: the
   * root-team reassignment deletes it on the server once every record has a
   * new Home team (NG-2156).
   */
  async forgetDeletedTeam(slug: string, id: string): Promise<void> {
    const cache = this.cache(slug);
    if (!cache) return;
    const { client, scope } = cache;
    await this.cancelReads(client, scope, (key) => isTeamsListKey(key) || key[2] === "team");
    client.removeQueries({ queryKey: teamDetailKey(scope, id) });
    this.patchLists(
      client,
      scope,
      (rows) => rows.filter((row) => row.id !== id),
      (page, rows) => rows.length - page.results.length,
    );
    await Promise.all([this.invalidateTree(slug), this.invalidateRosters(client, scope, [])]).catch(
      this.logCacheFailure("re-reading the team lists", { slug, id }),
    );
  }

  /** What deleting `id` would touch (NG-2157). Not cached: the delete dialog asks
   *  each time it opens, because the answer is what decides its screen. */
  fetchDeletePreflight(slug: string, id: string): Promise<TTeamDeletePreflight> {
    return this.rootStore.workspaceTeamService.deletePreflight(slug, id);
  }

  /** Admin-only, uncached — the graph is inspected ad hoc per user (spike). */
  async fetchPermissionGraph(slug: string, userId: string): Promise<TTeamPermissionGraph> {
    return this.rootStore.workspaceTeamService.getPermissionGraph(slug, userId);
  }

  /**
   * Set a team's whole roster. The answer becomes its cached roster (R17), and
   * what embeds rosters re-reads.
   */
  async setMembers(
    slug: string,
    teamId: string,
    memberIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<TTeamMember[]> {
    const cache = this.cache(slug);
    // The call sends the whole list, so count what it adds and removes against
    // the roster already read. A roster never read counts only the adds.
    const loaded = cache?.client.getQueryData<TTeamMember[]>(teamMembersKey(cache.scope, teamId));
    const res = await this.rootStore.workspaceTeamService.setMembers(slug, teamId, memberIds);
    const { added, removed } = listChanges(
      (loaded ?? []).map((member) => member.user),
      memberIds,
    );
    if (added > 0) track("team_member_added", { team_id: teamId, count: added, source });
    if (removed > 0) track("team_member_removed", { team_id: teamId, count: removed, source });
    if (cache) {
      const { client, scope } = cache;
      await this.cancelReads(client, scope, isRosterKey);
      client.setQueryData<TTeamMember[]>(teamMembersKey(scope, teamId), res);
      this.invalidateRosters(client, scope, []).catch(
        this.logCacheFailure("re-reading the rosters", { slug, teamId }),
      );
    }
    return res;
  }

  /**
   * Change one member's role on the named teams, from the member axis
   * (`role: null` leaves a team). Answers with every team they are on after it.
   * The named teams' rosters (R17) and the member's team list (R19) re-read:
   * the member axis returns teams, not roster rows.
   */
  async changeMemberTeams(
    slug: string,
    memberId: string,
    changes: TMemberTeamChange[],
    source: AnalyticsSource = "page",
  ): Promise<TMemberTeam[]> {
    const res = await this.rootStore.workspaceTeamService.changeMemberTeams(
      slug,
      memberId,
      changes,
    );
    // A person's teams, set from their side: which teams changed, as a count.
    track("workspace_member_updated", {
      member_id: memberId,
      fields: ["teams"],
      count: changes.length,
      source,
    });
    const cache = this.cache(slug);
    if (cache) {
      const { client, scope } = cache;
      await this.cancelReads(client, scope, (key) => isRosterKey(key) || isMemberTeamsKey(key));
      this.invalidateRosters(
        client,
        scope,
        changes.map(({ team }) => team),
      ).catch(this.logCacheFailure("re-reading the rosters", { slug, memberId }));
    }
    return res;
  }

  async removeMember(
    slug: string,
    teamId: string,
    teamMemberId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workspaceTeamService.removeMember(slug, teamId, teamMemberId);
    track("team_member_removed", { team_id: teamId, count: 1, source });
    const cache = this.cache(slug);
    if (!cache) return;
    const { client, scope } = cache;
    await this.cancelReads(client, scope, isRosterKey);
    client.setQueryData<TTeamMember[]>(teamMembersKey(scope, teamId), (members) =>
      members?.filter((member) => member.id !== teamMemberId),
    );
    this.invalidateRosters(client, scope, []).catch(
      this.logCacheFailure("re-reading the rosters", { slug, teamId }),
    );
  }
}
